import { FloatingPortal } from "@floating-ui/react";
import { Combobox, Dialog } from "@headlessui/react";
import classNames from "classnames";
import Button from "@/components/base/CodeCombobox/Button";
import CodeComboboxResults from "@/components/base/CodeCombobox/CodeComboboxResults";
import useCodeCombobox from "@/components/base/CodeCombobox/useCodeCombobox";
import CodingCardList from "@/components/base/CodingCard/CodingCardList";
import FieldArray from "@/components/forms/FieldArray";
import TD from "@/components/forms/TopDownForm";
import { ICoding } from "@/data-models/value-models";
import { IHumanReadableCoding } from "@/data-models/value-models/types";
import groupBy from "lodash/groupBy";
import React from "react";
import { DeepPartial, FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import client, { CreateValueSetDoc } from "@/services/content/client";

type FieldValues = {
  options: ICoding[];
  title: string;
  description: string;
  url: string;
};
interface MakeValueSetFormProps {
  defaultValues: DeepPartial<FieldValues>;
  close: () => void;
}

function titleToUrl(title: string) {
  return (
    "http://tiro.health/fhir/ValueSet/" +
    title.toLowerCase().split(/\W+/).join("-")
  );
}

function optionsToCompose(options: ICoding[]) {
  return {
    include: Object.entries(groupBy(options, (c) => c.system)).map(
      ([system, codes]) => ({
        system,
        concept: codes.map((c) => ({ code: c.code, display: c.display })),
      }),
    ),
  };
}

export function FormDialog({
  isOpen,
  close,
  children,
}: {
  isOpen: boolean;
  close: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={isOpen} onClose={close}>
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title className="text-lg font-semibold">
            Create a ValueSet
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm">
            Make a ValueSet of the current set of options to reuse in other
            places.
          </Dialog.Description>
          {children}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export function MakeValueSetForm({
  defaultValues,
  close,
}: MakeValueSetFormProps) {
  const methods = useForm<FieldValues>({
    defaultValues,
    shouldUseNativeValidation: true,
  });
  const {
    watch,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = methods;
  const { mutate } = useMutation((requestBody: CreateValueSetDoc) =>
    client.r5.createValuesetR5({ requestBody }),
  );
  const submit = (values: FieldValues) => {
    mutate({
      ...values,
      status: "draft",
      compose: optionsToCompose(values.options),
    });
    close();
  };
  const handleReset = () => {
    reset();
    close();
  };
  const title = watch("title");
  const url = title && titleToUrl(title);
  return (
    <form onSubmit={handleSubmit(submit)} onReset={handleReset}>
      <FormProvider {...methods}>
        <TD.Field
          label="Title"
          {...register("title", { required: true })}
          error={!!errors.title}
        />
        <TD.Field
          type="text"
          className="bg-gray-50 font-mono"
          tabIndex={-1}
          readOnly
          {...register("url")}
          value={url}
          extraSmall
          error={!!errors.url}
        />
        <TD.Field
          {...register("description", {
            required: true,
            minLength: 10,
          })}
          label="Description"
          error={!!errors.description}
        />
        <FieldArray<FieldValues> name="options">
          {({ fields, remove, append }) => (
            <>
              <CodingCardList codes={fields} onCodeRemove={remove} />
              <CodingComboboxInput
                onCodeChange={(code) => code && append(code)}
                valueSet="http://tiro.health/fhir/ValueSet/snomed-ct-auto-generated"
              />
            </>
          )}
        </FieldArray>
        <div className="mt-4 flex justify-end gap-2">
          <TD.Button type="reset" style="outline">
            Cancel
          </TD.Button>
          <TD.Button type="submit">Save</TD.Button>
        </div>
      </FormProvider>
    </form>
  );
}

export default MakeValueSetForm;

type CodingComboboxProps = {
  className?: string;
  code?: IHumanReadableCoding | null;
  onCodeChange?: (code: IHumanReadableCoding | null) => void;
  valueSet: string;
};

function CodingComboboxInput({
  valueSet,
  code = null,
  onCodeChange,
  className,
  ...props
}: CodingComboboxProps) {
  const {
    query,
    filteredCodes,
    handleQueryChange,
    by,
    displayValue,
    handleCodeChange,
    referenceProps,
    floatingProps,
    fetchStatus,
    helpers,
  } = useCodeCombobox(valueSet, {
    allowedPlacements: ["bottom-start", "bottom-end"],
  });

  return (
    <Combobox
      as="div"
      className={classNames(
        "inline-flex max-w-md items-baseline justify-between",
        className,
      )}
      value={code}
      onChange={handleCodeChange(onCodeChange)}
      by={by}
      {...props}
    >
      {({ open }) => (
        <div
          {...referenceProps}
          className={classNames("relative grow rounded-md shadow-sm")}
        >
          <Combobox.Input
            autoComplete="off"
            className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm sm:leading-6"
            value={query}
            onChange={handleQueryChange}
            displayValue={displayValue}
          />
          <Combobox.Button
            as={Button}
            loading={fetchStatus.isFetching}
            error={fetchStatus.isError}
            success={fetchStatus.isSuccess}
          />
          <FloatingPortal>
            <CodeComboboxResults
              {...floatingProps}
              open={open && query.trim().length > 0}
              minimal
              codes={filteredCodes}
              helpers={helpers}
            />
          </FloatingPortal>
        </div>
      )}
    </Combobox>
  );
}
